import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import SampleCategory from '../../../../../types/sample-category/SampleCategory';
import SampleCategoryWriteDTO from '../../../../../types/sample-category/SampleCategoryWriteDTO';
import SampleType from '../../../../../types/sample-type/SampleType';
import SampleTypeWriteDTO from '../../../../../types/sample-type/SampleTypeWriteDTO';
import { CanManageSampleDefinitionsDirective } from '../../../directives/can-manage-sample-definitions.directive';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { SampleCategoryService } from '../../../services/sample-category.service';
import { SampleTypeService } from '../../../services/sample-type.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import {
    CreateSampleCategoryDialogComponent
} from '../../shared/sample-categories/create-sample-category-dialog/create-sample-category-dialog.component';
import {
    UpdateSampleCategoryDialogComponent
} from '../../shared/sample-categories/update-sample-category-dialog/update-sample-category-dialog.component';
import {
    UpdateSampleTypeDialogComponent
} from '../../shared/sample-types/update-sample-type-dialog/update-sample-type-dialog.component';

@Component({
    selector: 'app-sample-type-detail',
    templateUrl: './sample-type-detail.component.html',
    styleUrls: [ './sample-type-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateSampleTypeDialogComponent,
        TableModule,
        SharedModule,
        CreateSampleCategoryDialogComponent,
        UpdateSampleCategoryDialogComponent,
        CanManageSampleDefinitionsDirective,
        Tooltip,
        RoleNamePipe
    ]
})
export class SampleTypeDetailComponent implements OnInit {
    public sampleType: SampleType | null = null;
    public sampleCategories: SampleCategory[] = [];
    public editableSampleType: SampleTypeWriteDTO = new SampleTypeWriteDTO({});
    public editableSampleCategory: SampleCategoryWriteDTO = new SampleCategoryWriteDTO({});
    public displayUpdateDialog = false;
    public displayUpdateCategoryDialog = false;
    public displayCreateCategoryDialog = false;

    constructor(
        private route: ActivatedRoute,
        private sampleTypeService: SampleTypeService,
        private sampleCategoryService: SampleCategoryService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.reloadSampleTypeDetails();
    }

    /**
     * Opens the edit dialog.
     */
    public openEditDialog(): void {
        this.editableSampleType = new SampleTypeWriteDTO(this.sampleType ?? {});
        this.displayUpdateDialog = true;
    }

    /**
     * Reloads the sample type details.
     */
    public reloadSampleTypeDetails(): void {
        this.loadSampleType();
        this.loadSampleTypeCategories();
    }

    /**
     * Confirms the deletion of the sample type.
     */
    public confirmDelete(): void {
        if (!this.sampleType) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete the sample type '${ this.sampleType.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (!this.sampleType) {
                    return;
                }
                this.sampleTypeService.deleteSampleType(this.sampleType.id).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: 'Sample type deleted successfully',
                        life: 3000
                    });
                    window.history.back();
                }).catch((error: any) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Loads the sample type.
     */
    private loadSampleType(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id, 10);
            this.sampleTypeService.getSampleType(idNumber).then((result: SampleType) => {
                this.sampleType = result;
            }).catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            });
        }
    }

    /**
     * Reloads the sample type categories.
     */
    private loadSampleTypeCategories(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id, 10);
            this.sampleCategoryService.getSampleCategoriesByType(idNumber)
                .then((result: SampleCategory[]) => {
                    this.sampleCategories = result;
                }).catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            });
        }
    }

    /**
     * Opens the edit dialog for a sample category.
     */
    public editCategory(category: SampleCategory): void {
        this.editableSampleCategory = new SampleCategoryWriteDTO(category);
        this.displayUpdateCategoryDialog = true;
    }

    /**
     * Deletes a sample category.
     */
    public deleteCategory(category: SampleCategory): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the sample category ' + category.name + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.sampleCategoryService.deleteSampleCategory(category.id).then(() => {
                    this.loadSampleTypeCategories();
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Sample category deleted', life: 3000 });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Open the create category dialog.
     */
    public openCreateCategoryDialog() {
        this.displayCreateCategoryDialog = true;
    }

}
